import { Alert, Button, Input, Select, Switch, Tooltip } from "antd";
import { Copy, Trash2 } from "lucide-react";
import { CODE_STEP, getStepDefinition, stepInputs, tableLabel } from "../../../../engine";
import { STEP_CATEGORY_COLORS } from "../../shared/display";
import { DeleteStep } from "../DeleteControls";
import ValueForm from "../ValueForm";

// Roadmap 2: a code step's settings. Its code is never shown; the summary
// says what it does and the values the user fills in are editable.
const CodeParams = ({ step, dispatch }) => {
  const { summary, valueFields = [], values = {} } = step.params || {};
  const setValue = (name, value) => dispatch({ type: "updateStepParams", id: step.id, params: { ...step.params, values: { ...values, [name]: value } } });
  return (
    <div className="space-y-3">
      {summary && <p className="text-[13px] text-gray-700">{summary}</p>}
      {valueFields.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-gray-600">Values (stay on your device)</div>
          <ValueForm fields={valueFields} values={values} onChange={setValue} />
          <div className="text-[11px] text-gray-400">Leave any value empty if it doesn't apply.</div>
        </div>
      )}
      <div className="text-[11px] text-gray-400">Written by Ledgerate AI. To change what this step does, describe it in the chat.</div>
    </div>
  );
};

const StepInspector = ({ step, workflow, run, dispatch }) => {
  const index = workflow.steps.findIndex((s) => s.id === step.id);
  const definition = getStepDefinition(step.type);
  if (!definition) return <Alert type="error" message={`Unknown step type "${step.type}"`} />;

  const inputIds = stepInputs(workflow, index);
  const result = run?.results.find((r) => r.stepId === step.id);
  const failed = run?.error?.stepId === step.id;
  const notRun = run && !result && !run.ok;

  const inputChoices = [
    ...workflow.sources.map((s) => ({ value: s.id, label: tableLabel(workflow, s.id) })),
    ...workflow.steps.slice(0, index).map((s) => ({ value: s.id, label: tableLabel(workflow, s.id) })),
  ];
  const multiInput = definition.inputs.max > 1;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color: STEP_CATEGORY_COLORS[definition.category] }}>
          Step {index + 1} · {definition.category}
        </div>
        <Input
          variant="borderless"
          className="!px-0 text-lg font-semibold"
          value={step.label ?? definition.label}
          onChange={(e) => dispatch({ type: "updateStep", id: step.id, changes: { label: e.target.value || undefined } })}
        />
        <p className="text-xs text-gray-500">{definition.description}</p>
      </div>

      {failed && <Alert type="error" showIcon message={result?.error} />}
      {notRun && <Alert type="info" showIcon message="Not run yet: an earlier step has an error." />}
      {result?.warnings?.map((warning) => <Alert key={warning} type="warning" showIcon message={warning} />)}

      <div>
        <div className="text-xs font-medium text-gray-600 mb-1">{multiInput ? "Read from (tables)" : "Read from"}</div>
        <Select
          className="w-full"
          mode={multiInput ? "multiple" : undefined}
          value={multiInput ? inputIds : inputIds[0]}
          placeholder="Not connected. Pick a table"
          options={inputChoices}
          onChange={(next) => dispatch({ type: "updateStep", id: step.id, changes: { inputs: [].concat(next) } })}
        />
        {!multiInput && !step.inputs && <div className="text-[11px] text-gray-400 mt-1">Defaults to the previous step</div>}
      </div>

      {step.type === CODE_STEP ? (
        <CodeParams step={step} dispatch={dispatch} />
      ) : (
        <Alert type="info" showIcon message="Built-in steps are switched off in this version. Ask Ledgerate AI to rebuild this step." />
      )}

      <div className="flex items-center justify-between border-t pt-3">
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <Switch size="small" checked={!step.disabled} onChange={(on) => dispatch({ type: "updateStep", id: step.id, changes: { disabled: !on } })} />
          Enabled
        </label>
        <div className="flex gap-1">
          <Tooltip title="Duplicate">
            <Button size="small" icon={<Copy size={14} />} onClick={() => dispatch({ type: "duplicateStep", id: step.id })} />
          </Tooltip>
          <DeleteStep step={step}>
            <Tooltip title="Delete step">
              <Button size="small" danger icon={<Trash2 size={14} />} />
            </Tooltip>
          </DeleteStep>
        </div>
      </div>
    </div>
  );
};

export default StepInspector;
